import SelectBox from 'devextreme-react/select-box';
import TextBox from 'devextreme-react/text-box';
import CustomStore from 'devextreme/data/custom_store';
import { useEffect, useMemo, useRef } from 'react';

export interface EntraGroupSearchResult {
  id: string;
  displayName: string;
}

export interface EntraGroupDisplayNameFieldProps {
  value: string | null;
  readOnly?: boolean;
  onValueChange: (displayName: string | null) => void;
  /** When provided, shows a Graph-backed group search (stores group display name). */
  searchGroups?: (query: string) => Promise<EntraGroupSearchResult[]>;
  placeholder?: string;
}

/**
 * Portal alert group field — persists Entra security group display name for PharmCollab scoping.
 */
export function EntraGroupDisplayNameField({
  value,
  readOnly = false,
  onValueChange,
  searchGroups,
  placeholder = 'Type to search client-tenant groups…'
}: EntraGroupDisplayNameFieldProps) {
  const groupCacheRef = useRef(new Map<string, EntraGroupSearchResult>());

  useEffect(() => {
    if (!value?.trim()) return;
    const name = value.trim();
    if (!groupCacheRef.current.has(name.toLowerCase())) {
      groupCacheRef.current.set(name.toLowerCase(), { id: name, displayName: name });
    }
  }, [value]);

  const groupStore = useMemo(() => {
    if (!searchGroups) return null;
    return new CustomStore({
      key: 'id',
      load: async (loadOptions) => {
        const q = String(loadOptions.searchValue ?? '').trim();
        if (q.length < 2) return [];
        try {
          const results = await searchGroups(q);
          for (const g of results) {
            groupCacheRef.current.set(g.id, g);
            if (g.displayName) groupCacheRef.current.set(g.displayName.toLowerCase(), g);
          }
          return results;
        } catch {
          return [];
        }
      },
      byKey: async (key) => {
        const cached = groupCacheRef.current.get(String(key));
        if (cached) return cached;
        const byName = groupCacheRef.current.get(String(key).toLowerCase());
        if (byName) return byName;
        return {
          id: String(key),
          displayName: String(key)
        };
      }
    });
  }, [searchGroups]);

  if (readOnly || !searchGroups || !groupStore) {
    return (
      <TextBox
        readOnly={readOnly}
        value={value ?? ''}
        placeholder={readOnly ? '—' : 'Entra group display name for Client Portal scoping'}
        onValueChanged={(e) =>
          onValueChange((e.value ?? '').trim() ? String(e.value).trim() : null)
        }
      />
    );
  }

  const selectedId =
    value != null && value !== ''
      ? groupCacheRef.current.get(value.toLowerCase())?.id ?? value
      : null;

  return (
    <SelectBox
      dataSource={groupStore}
      displayExpr="displayName"
      valueExpr="id"
      searchEnabled
      minSearchLength={2}
      searchTimeout={300}
      readOnly={readOnly}
      placeholder={placeholder}
      noDataText="No groups found — type at least 2 characters"
      showClearButton={!readOnly}
      value={selectedId}
      onValueChanged={(e) => {
        const id = e.value as string | null | undefined;
        if (!id) {
          onValueChange(null);
          return;
        }
        const picked = groupCacheRef.current.get(id);
        onValueChange(picked?.displayName?.trim() || String(id).trim() || null);
      }}
    />
  );
}
